import { useCallback, useRef, useState } from "react";
import {
  buildPlanStarter,
  lastLoggedKgsFromWorkouts,
  buildProgramFromPlanId,
  resolveProgramSession,
  type Exercise,
  type PlanStarter,
  type Plan as PlanT,
  type PlannedExerciseEntry,
  type Program as ProgramT,
} from "@jackedlogs/shared";
import {
  ensureRecentWorkoutsCached,
  useLocale,
  useMaxesData,
} from "@jackedlogs/ui";

import { apiFetch } from "./api";

// Web twin of apps/mobile/src/screens/useWorkoutStarter.ts (RW-18 follow-up:
// the logger has been on web since 2026-08-14, but nothing built a
// PlanStarter for it yet — see next/rnw-strangler.md's "Start on Plan is
// still stub" note, 2026-08-16). Same shared buildPlanStarter/
// resolveProgramSession/lastLoggedKgsFromWorkouts mobile uses; only the
// hand-off differs. Mobile pushes into `usePendingStart` and navigates
// across tabs; web-rnw's LoggerScreen already accepts `planStarter` as a
// direct prop, so the caller just hands the built starter to `onStarter`
// (AppShell wires it into a composerRequest + tab switch, mirroring the
// Create-hub's own "New workout" dispatch) — no cross-navigation state
// machine needed.
export function useWorkoutStarter(
  catalog: Exercise[] | null,
  bodyweightKg: number | null,
  onStarter: (starter: PlanStarter) => void,
): {
  starting: string | null;
  startPlan: (plan: PlanT) => Promise<void> | undefined;
  startProgramSession: (
    program: ProgramT,
    weekIdx: number,
    sessionIdx: number,
  ) => Promise<void> | undefined;
} {
  // Same shared MAX data layer the Maxes console reads (useMaxesData.ts) —
  // TTL-cached + in-flight-coalesced, so a second consumer on the same
  // screen (e.g. the logger's own host deps) doesn't double-fetch.
  const { getFresh: getMaxes } = useMaxesData({ apiFetch });
  const lang = useLocale();
  const [starting, setStarting] = useState<string | null>(null);
  // Ref guard so a double-tap can't fire two launches before state settles
  // (mirrors mobile's identical guard).
  const inFlight = useRef(false);

  const launch = useCallback(
    async (
      id: string,
      planId: string,
      name: string,
      notes: string | undefined,
      entries: PlannedExerciseEntry[],
      coachNotes?: { programDescription?: string; sessionNote?: string },
    ) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setStarting(id);
      try {
        const [maxes, recentWorkouts] = await Promise.all([
          getMaxes(),
          ensureRecentWorkoutsCached(apiFetch),
        ]);
        const slugs = entries.map((e) => e.exerciseId);
        const lastLoggedKgs = lastLoggedKgsFromWorkouts(recentWorkouts, slugs);
        const starter = buildPlanStarter({
          planId,
          name,
          ...(notes ? { notes } : {}),
          ...(coachNotes ? { coachNotes } : {}),
          startedAtLocal: new Date().toISOString().slice(0, 16),
          entries,
          ctx: {
            maxes,
            bodyweightKg,
            lastLoggedKgs,
            catalogItems: catalog ?? [],
          },
        });
        onStarter(starter);
      } finally {
        inFlight.current = false;
        setStarting(null);
      }
    },
    [getMaxes, catalog, bodyweightKg, onStarter],
  );

  // Single-day plan (Library "Mine" tab) — mirrors mobile's identical
  // startPlan, same launch() engine as startProgramSession below.
  const startPlan = useCallback(
    (p: PlanT) =>
      launch(
        p.id,
        p.id,
        p.name,
        p.notes,
        p.entries,
        p.notes ? { sessionNote: p.notes } : undefined,
      ),
    [launch],
  );

  const startProgramSession = useCallback(
    (program: ProgramT, weekIdx: number, sessionIdx: number) => {
      const resolved = resolveProgramSession({
        program,
        weekIdx,
        sessionIdx,
        locale: lang,
      });
      if (!resolved) return;
      // PE-21 — the RESOLVED name, not the raw program field: this
      // string is persisted as the workout's name, so a per-locale
      // object here would have been written to history as
      // "[object Object]" with nothing to catch it.
      const name = `${resolved.programName} · W${weekIdx + 1} · ${resolved.sessionName}`;
      // MC-8 — the composite carries the stable session id (reorder-safe),
      // with the ordinal as a fallback rail. Built by the one shared helper.
      const planId = buildProgramFromPlanId(
        program.id,
        weekIdx,
        sessionIdx,
        resolved.sessionId,
      );
      const coachNotes =
        resolved.programDescription || resolved.sessionNotes
          ? {
              ...(resolved.programDescription
                ? { programDescription: resolved.programDescription }
                : {}),
              ...(resolved.sessionNotes
                ? { sessionNote: resolved.sessionNotes }
                : {}),
            }
          : undefined;
      return launch(
        program.id,
        planId,
        name,
        undefined,
        resolved.entries,
        coachNotes,
      );
    },
    [launch, lang],
  );

  return { starting, startPlan, startProgramSession };
}
